import { Component, Input, OnInit } from '@angular/core';
import { OrderWrapperComponent } from "../../component/wrapper/order-wrapper.component";
import { Store } from '@ngrx/store';
import { Cart, OrderItem, OrderPublic } from '../../../../globalTypes/order.types';
import { lastValueFrom, take, takeUntil } from 'rxjs';
import { ordersFeature, OrdersActions } from '../../store/order.store';
import { OrderService } from '../../services/order.service';
import { Base } from '../../component/wrapper/base-wrapper.component';
import { Logger } from '../../helper/logger';
import { accountFeature } from '../../store/account.store';
import { LocationPublic } from '../../../../globalTypes/account.types';
import { CurrencyPipe, JsonPipe } from '@angular/common';
import { LOCATION_LABEL } from '../../../../globalTypes/configs';
import { NameTagComponent } from "../../component/layout/element/name.tag.component";
import { QuantityStepperComponent } from "../../component/layout/element/quantity-stepper.component";
import { removeAllItemsFromCart, RemoveAllItemsFromCartVariables, removeItemFromCart, RemoveItemFromCartVariables } from '@dataconnect/account';
import { Router, RouterLink } from '@angular/router';
import { ButtonComponent } from "../../component/layout/element/button.component";
import { ConfirmDeleteDialogComponent, ConfirmDeleteDialogData } from '../../helper/confirm.delete.dialog';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LocationWarning } from "../../component/layout/element/location.warning";
import { UpdateItemQuantityInCartOnCallVariables } from '../../../../globalTypes/onCall.types';
const log = new Logger("CartComponent");

@Component({
  selector: 'msh-cart',
  imports: [CurrencyPipe, RouterLink, NameTagComponent, QuantityStepperComponent, ButtonComponent, LocationWarning],
  template: `
  <msh-location-warning></msh-location-warning>
  @if(order && order.orderItems_by_location){
    <div class="m-auto max-w-[650px]">
    @for (locationId of locationIdsWithItems; track locationId) {
      <div class="text-center text-sm pb-4">
      @if(locations[locationId] && locations[locationId].name){
        <msh-name-tag 
          [label]="locations[locationId].name"
          [url]="locations[locationId].profileImage?.url"
          [size]="'small'"
        ></msh-name-tag>
        
      } @else {
        Inactive {{LOCATION_LABEL}} (<span class="text-xs">id: {{locationId}}</span>)
      }
      </div>

      <!-- loop items -->
      @if(order && order.orderItems_by_location && order.orderItems_by_location[locationId]; as items){
        @for (item of items; track item.id) {
          <div class="pb-5 flex gap-1">
            <div class=" w-16 h-16 mr-6 rounded overflow-hidden">
              <!-- TODO: HANDLE image === null -->
              @if(item.listingVariation.image){
                <img 
                  [src]="item.listingVariation.image.url"
                  class=""
                />
              }
            </div>
            <div>
              <div class="font-bold">{{item.listingVariation.name}}</div>
              <div class="text-sm">{{item.listingVariation.description}}</div>
              <div>{{item.pricePerItemAtTimeOfOrder | currency}}</div>
            </div>
            <div class="grow"></div>
            <msh-quantity-stepper 
              [removable]="true"
              [min]="0" 
              [max]="item.isDigitalDownload ? 1 : item.listingVariation.quantity" [value]="item.quantity"
              (valueChange)="onQuantityChange($event, item)"
              (onRemove)="removeItem(item)"
            ></msh-quantity-stepper>
          </div>
        }
      }
    }
    @if(standAlone){
      <msh-button (onClick)="goToCheckout()" cta="Checkout"></msh-button>
    }
    <div class="lil-action mt-6" (click)="clearCart()">Clear cart</div>
    </div>
  } @else {
    <div class="null mt-16 mb-6">Your cart is empty.  Search the <a class="text-orange-500 font-bold" [routerLink]="['/listing']">listings</a> to add something to your cart</div>
    <div class="null">Or check the status of your <a class="text-orange-500 font-bold" [routerLink]="['/order/purchase']"> previous purchases</a>...</div>
    <div class="null">or have a look at <a class="text-orange-500 font-bold" [routerLink]="['/order/fulfillment']">your fulfillments</a>.</div>
  }
  `,
  styles: ``
})
export class CartComponent extends Base<CartComponent> implements OnInit {
  @Input() standAlone = false;
  locationId = '';
  locations: {[locationId: string]: LocationPublic} = {}; 
  locationIdsWithItems: string[] = []
  order?: OrderPublic
  LOCATION_LABEL = LOCATION_LABEL

  constructor(
    private orderService: OrderService,
    private store: Store,
    private dialog: MatDialog,
    private router: Router,
    private snackBar: MatSnackBar,
  ){
    super();
    log.log('listen for cart')

    this.store.select(accountFeature.selectLocations).pipe(takeUntil(this.$destroy)).subscribe(locations => {
      this.locations = locations as {[locationId:string]: LocationPublic}
    })
    this.store.select(ordersFeature.selectCart).pipe(takeUntil(this.$destroy)).subscribe(cart => {
      log.log({cart})
      if(!cart) return;
      if(Object.keys(cart.orderItems_on_order || []).length === 0){
        this.order = undefined
        return;
      }
      this.order = cart as OrderPublic

      log.log({"items on order": Object.keys(this.order.orderItems_on_order).length})
      if(this.order && this.order.orderItems_by_location){
        this.locationIdsWithItems = Object.keys(this.order.orderItems_by_location)
      }

    })
  }

  async ngOnInit(): Promise<void> {
    this.store.select(accountFeature.selectLocation).pipe(takeUntil(this.$destroy)).subscribe(location => {
      if(!location) return;
      this.locationId = location.id;
    });
  }

  async onQuantityChange(ev: any, item: OrderItem){
    const itemQuantityVars: UpdateItemQuantityInCartOnCallVariables = {
      orderItemId: item.id,
      orderId: item.orderId,
      quantity: ev,
      locationId: this.locationId,
    }
    this.orderService.updateItemQuantityAndHydrate(itemQuantityVars);
  }

  async removeItem(item: OrderItem){
    log.log({remove: item})
    const removeItemVars: RemoveItemFromCartVariables = {
      orderItemId: item.id,
      orderId: item.orderId,
      locationId: this.locationId,
    }
    try {
      const resultRef = await removeItemFromCart(removeItemVars);
      if(resultRef){
        log.log({resultRef});
        await this.orderService.hydrateCart(item.orderId, this.locationId);
      }
    } catch(error){
      log.error({error})
      this.snackBar.open("There was an error removing the item from your cart")
    }
  }

  async clearCart(){
    const amountInCart = this.order?.orderItems_on_order.length || 0;
    const stash = (amountInCart === 1 ? "this item" : amountInCart + " items" )
    const data: ConfirmDeleteDialogData = {
      item: "Cart",
      action: "Clear",
      info: ["Remove "+stash+" from cart"],
      class: '',
    }
    const dialog = this.dialog.open(ConfirmDeleteDialogComponent, {data});
    const result = await lastValueFrom(dialog.afterClosed().pipe(take(1)));
    if(result && this.order && this.order.id){
      this.store.dispatch(OrdersActions.toggleSideCart({isOpen: false}))
      const removeAllVars: RemoveAllItemsFromCartVariables = {orderId: this.order.id, locationId: this.locationId}
      log.log({removeAllVars})
      const resultRef = await removeAllItemsFromCart(removeAllVars)
      log.log({resultRef});
      await this.orderService.hydrateCart(this.order.id, this.locationId);
    }
  }

  goToCheckout(){
    this.router.navigate(['order','checkout'])
  }
}
